/** ConfirmDialog — generic confirmation modal. */

import type { ReactNode } from "react";
import { AlertTriangle, Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, type ButtonProps } from "@/components/ui/button";

type Tone = "default" | "warning" | "danger";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
  confirmVariant?: ButtonProps["variant"];
  icon?: ReactNode;
  onConfirm: () => void | Promise<void>;
  pending?: boolean;
}

const TONE_ICON: Record<Tone, ReactNode> = {
  default: <Info className="h-6 w-6 text-primary" />,
  warning: <AlertTriangle className="h-6 w-6 text-warning" />,
  danger:  <AlertTriangle className="h-6 w-6 text-danger" />,
};

const TONE_VARIANT: Record<Tone, ButtonProps["variant"]> = {
  default: "default",
  warning: "warning",
  danger:  "destructive",
};

const TONE_RING: Record<Tone, string> = {
  default: "bg-primary/10",
  warning: "bg-warning/10",
  danger:  "bg-danger/10",
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",
  confirmVariant,
  icon,
  onConfirm,
  pending = false,
}: ConfirmDialogProps) {
  const resolvedIcon = icon ?? TONE_ICON[tone];
  const resolvedVariant = confirmVariant ?? TONE_VARIANT[tone];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Block close while a mutation is in flight so the user doesn't see
        // the dialog vanish mid-request.
        if (!o && pending) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <div className="flex flex-col items-center text-center">
          <div
            className={`grid h-12 w-12 place-items-center rounded-full ${TONE_RING[tone]}`}
          >
            {resolvedIcon}
          </div>
          <DialogHeader className="mb-1.5 mt-3 pr-0">
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          {description ? (
            <div className="text-sm leading-relaxed text-fg-muted">
              {description}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={resolvedVariant}
            onClick={() => void onConfirm()}
            loading={pending}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
